import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, FileText } from 'lucide-react';
import { fetchKbSourceChunks } from '../../../api/ivrAgents.js';
import { MarkdownText } from './MarkdownText.jsx';
import { overlayDismiss } from '../../../lib/overlayDismiss';

/**
 * SourcePreviewModal — 點來源檔名後彈出，逐 chunk 顯示該檔的知識庫內文。
 *   - 每個 chunk 一張卡片：標頭（區塊序號 + 字數）+ question（粗體標題）+ answer（markdown）
 *   - 有 images 的 chunk（KB 多媒體圖卡）一併縮圖顯示
 *   - 純讀取，不改任何資料
 */
export function SourcePreviewModal({ open, onClose, kbId, sourceId, fileName, avatarId = null }) {
    const { t } = useTranslation('workspace');
    const [chunks, setChunks] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!open || !kbId || !sourceId) return;
        let cancelled = false;
        setLoading(true);
        setError(null);
        setChunks([]);
        fetchKbSourceChunks(kbId, sourceId, avatarId)
            .then((r) => { if (!cancelled) setChunks(r.chunks || []); })
            .catch((e) => { if (!cancelled) setError(e.message); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [open, kbId, sourceId, avatarId]);

    if (!open) return null;

    const displayName = stripUploadPrefix(fileName) || t('sources.unnamed');

    return (
        <div className="fixed inset-0 bg-ink-900/30 backdrop-blur-sm z-50 grid place-items-center p-4" {...overlayDismiss(onClose)}>
            <div
                className="bg-cream-50 border border-cream-200 rounded-lg shadow-xl w-full max-w-3xl max-h-[85vh] flex flex-col"
                onClick={(e) => e.stopPropagation()}
            >
                {/* header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-cream-200 shrink-0">
                    <div className="min-w-0">
                        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-400">
                            {t('sources.previewTitle')}
                        </div>
                        <h2 className="heading-display text-lg font-semibold text-ink-900 mt-0.5 flex items-center gap-2 truncate" title={fileName}>
                            <FileText size={16} className="text-ink-400 shrink-0" strokeWidth={1.5} />
                            <span className="truncate">{displayName}</span>
                        </h2>
                    </div>
                    <button
                        onClick={onClose}
                        className="size-8 inline-flex items-center justify-center rounded-md text-ink-400 hover:text-ink-700 hover:bg-cream-100 transition shrink-0"
                    >
                        <X size={16} />
                    </button>
                </div>

                {/* body */}
                <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3">
                    {loading && (
                        <div className="text-center text-ink-400 text-sm py-10 italic">
                            {t('sources.previewLoading')}
                        </div>
                    )}
                    {!loading && error && (
                        <div className="text-xs text-danger-500 bg-danger-100/40 border border-danger-500/30 rounded-md px-3 py-2">
                            {t('sources.previewError', { error })}
                        </div>
                    )}
                    {!loading && !error && chunks.length === 0 && (
                        <div className="text-center text-ink-400 text-sm py-10 italic">
                            {t('sources.previewEmpty')}
                        </div>
                    )}
                    {!loading && !error && chunks.map((c, i) => (
                        <ChunkCard key={c.id} chunk={c} index={i + 1} t={t} />
                    ))}
                </div>

                {/* footer */}
                {!loading && !error && chunks.length > 0 && (
                    <div className="border-t border-cream-200 px-6 py-2.5 shrink-0">
                        <div className="font-mono text-[10px] uppercase tracking-wider text-ink-400">
                            {t('sources.previewCount', { count: chunks.length })}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

function ChunkCard({ chunk, index, t }) {
    const { question, answer, chars, images } = chunk;
    return (
        <div className="border border-cream-200 rounded-lg bg-white overflow-hidden">
            <div className="flex items-center justify-between px-3 py-1.5 bg-cream-100/60 border-b border-cream-200">
                <span className="font-mono text-[11px] font-semibold text-ink-600">
                    {t('sources.previewChunk', { index })}
                </span>
                <span className="font-mono text-[10px] text-ink-400">
                    {t('sources.previewChars', { count: chars })}
                </span>
            </div>
            <div className="px-3 py-2.5 space-y-1.5">
                {question && (
                    <div className="text-sm font-semibold text-ink-900">{question}</div>
                )}
                {answer && (
                    <div className="text-sm text-ink-700 leading-relaxed">
                        <MarkdownText>{answer}</MarkdownText>
                    </div>
                )}
                {Array.isArray(images) && images.length > 0 && (
                    <div className="flex flex-wrap gap-2 pt-1">
                        {images.map((img, k) => {
                            const src = typeof img === 'string' ? img : (img?.url || img?.src);
                            if (!src) return null;
                            return (
                                <a key={k} href={src} target="_blank" rel="noreferrer">
                                    <img
                                        src={src}
                                        alt=""
                                        className="h-20 w-auto rounded border border-cream-200 object-cover"
                                        loading="lazy"
                                    />
                                </a>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
}

function stripUploadPrefix(name) {
    if (!name) return '';
    return name.replace(/^\d{10,14}-[a-f0-9]{6,10}-/, '');
}
